import Phaser from 'phaser';
import { ts } from '../utils/textStyle';
import { GAME_HEIGHT, GAME_WIDTH, PLAYER_RADIUS, PLAYER_SPEED } from '../config/game';
import { EnemyDamagePayload, EnemySystem } from '../systems/EnemySystem';
import { PartnerSystem } from '../systems/PartnerSystem';
import { MaterialSystem, type SpecialDropPickupPayload } from '../systems/MaterialSystem';

import { VirtualJoystick } from '../systems/VirtualJoystick';
import { DebugOverlay, isDebugEnabled } from '../systems/debugOverlay';
import { DamageSource, RunState } from '../systems/runState';
import { SeededRandom, getSeedFromUrl } from '../systems/seededRandom';
import { ShopAutomationStrategy, ShopSystem } from '../systems/ShopSystem';
import { getDefaultPlayerTemplate, getPlayerTemplate, PlayerTemplateDefinition } from '../systems/PlayerTemplate';
import { RelicSystem } from '../systems/RelicSystem';
import { DevTuningMenu } from '../systems/DevTuningMenu';
import { PostGameRecap } from '../systems/PostGameRecap';
import { TooltipOverlay } from '../systems/TooltipOverlay';
import { getTunables } from '../config/tunables';
import { UpgradeOption, UpgradeSystem } from '../systems/UpgradeSystem';
import { RENDER_SCALE } from '../config/rendering';
import type { PartnerType } from '../systems/shopCatalog';
import { ThreeBattleView } from '../three/ThreeBattleView';

type WasdKeys = {
  W: Phaser.Input.Keyboard.Key;
  A: Phaser.Input.Keyboard.Key;
  S: Phaser.Input.Keyboard.Key;
  D: Phaser.Input.Keyboard.Key;
};

type BossDeathPayload = {
  x: number;
  y: number;
  mode?: 'wave' | 'debug';
  wave?: number;
  source?: PartnerType | 'relic';
};

type AutoplayResult = {
  status: 'running' | 'victory' | 'dead';
  seed: string;
  preset: string;
  strategy: ShopAutomationStrategy;
  template: string;
  wave: number;
  hp: number;
  maxHp: number;
  materials: number;
  level: number;
  elapsedMs: number;
  movementSamples: number;
  nearestEnemyMinDistance: number | null;
  stats: ReturnType<RunState['getRunStats']>;
};

type AutoplayConfig = {
  enabled: boolean;
  strategy: ShopAutomationStrategy;
  preset: string;
};

export class GameScene extends Phaser.Scene {
  private playerTemplate: PlayerTemplateDefinition = getDefaultPlayerTemplate();
  private player?: Phaser.GameObjects.Arc;
  private joystick?: VirtualJoystick;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd?: WasdKeys;
  private enemySystem?: EnemySystem;
  private partnerSystem?: PartnerSystem;
  private materialSystem?: MaterialSystem;
  private shopSystem?: ShopSystem;
  private relicSystem?: RelicSystem;
  private devTuningMenu?: DevTuningMenu;
  private postGameRecap?: PostGameRecap;
  private tooltipOverlay?: TooltipOverlay;
  private upgradeSystem?: UpgradeSystem;
  private upgradePanel?: Phaser.GameObjects.Container;
  private currentUpgradeOptions: UpgradeOption[] = [];
  private materialText?: Phaser.GameObjects.Text;
  private threeBattleView?: ThreeBattleView;

  private runState?: RunState;
  private debugOverlay?: DebugOverlay;
  private readonly moveVector = new Phaser.Math.Vector2(0, 0);
  private enemyText?: Phaser.GameObjects.Text;
  private hpText?: Phaser.GameObjects.Text;
  private waveText?: Phaser.GameObjects.Text;
  private waveBox?: Phaser.GameObjects.Rectangle;
  private materialBox?: Phaser.GameObjects.Rectangle;
  private statusPanel?: Phaser.GameObjects.Container;
  private damageCooldownMs = 0;
  private lastPhase?: string;
  private temporaryMoveSpeedBonus = 0;
  private temporaryMoveSpeedExpiresAt = 0;
  private partnerShieldCharges = 0;
  private readonly autoplayConfig: AutoplayConfig = getAutoplayConfig();
  private readonly autoplayVector = new Phaser.Math.Vector2(0, 0);
  private autoplayStartedAt = 0;
  private autoplayMovementSamples = 0;
  private autoplayNearestEnemyMinDistance = Infinity;
  private autoplayResultPublished = false;
  private autoplayLastStatusAt = 0;

  constructor() {
    super('game');
  }

  init(data: { templateId?: string }) {
    this.playerTemplate = getPlayerTemplate(data.templateId);
  }

  create() {
    this.cameras.main.setBackgroundColor(this.shouldUseThreeBattleView() ? 'rgba(0,0,0,0)' : '#17140f');
    this.cameras.main.setOrigin(0, 0);
    this.cameras.main.setZoom(RENDER_SCALE);
    this.cameras.main.setScroll(0, 0);
    this.physics.world.setBounds(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.drawArena();
    this.createHud();

    this.player = this.add.circle(GAME_WIDTH / 2, GAME_HEIGHT * 0.64, PLAYER_RADIUS, this.playerTemplate.color, 1);
    this.player.setStrokeStyle(3, this.playerTemplate.strokeColor, 0.95);
    this.player.setDepth(20);
    this.physics.add.existing(this.player);

    const playerBody = this.player.body as Phaser.Physics.Arcade.Body;
    playerBody.setCircle(PLAYER_RADIUS);
    playerBody.setOffset(-PLAYER_RADIUS, -PLAYER_RADIUS);
    playerBody.setCollideWorldBounds(true);
    playerBody.setAllowGravity(false);

    this.joystick = new VirtualJoystick(this);
    this.cursors = this.input.keyboard?.createCursorKeys();
    this.wasd = this.input.keyboard?.addKeys('W,A,S,D') as WasdKeys | undefined;
    const seed = getSeedFromUrl();
    const rng = new SeededRandom(seed);
    this.runState = new RunState(seed, this.playerTemplate);
    this.runState.start();
    this.autoplayStartedAt = this.time.now;
    this.relicSystem = new RelicSystem();
    this.upgradeSystem = new UpgradeSystem(seed);
    this.enemySystem = new EnemySystem(this, this.player, rng);
    this.enemySystem.startWave(this.runState.wave);
    this.debugOverlay = new DebugOverlay(this);
    this.debugOverlay.setVisible(isDebugEnabled());
    this.partnerSystem = new PartnerSystem(this, this.player, this.enemySystem, {
      damageMultiplier: this.playerTemplate.modifiers.partnerDamageMultiplier,
      critChanceBonus: this.playerTemplate.modifiers.critChanceBonus,
    }, this.relicSystem);
    this.materialSystem = new MaterialSystem(this, this.player, rng, this.playerTemplate.modifiers.pickupRadiusMultiplier);
    this.shopSystem = new ShopSystem(this, this.runState, this.partnerSystem, this.relicSystem, seed, {
      rerollDiscount: this.playerTemplate.modifiers.rerollDiscount,
    });
    this.devTuningMenu = new DevTuningMenu();
    this.devTuningMenu.mount();
    this.postGameRecap = new PostGameRecap();
    this.tooltipOverlay = new TooltipOverlay();
    this.tooltipOverlay.mount();
    this.events.on('enemy-damaged', this.recordDamageDealt, this);
    this.events.on('enemy-death-stats', this.recordEnemyDefeated, this);
    this.events.on('boss-died', this.recordBossDefeated, this);
    this.events.on('material-picked', this.recordMaterialPickup, this);
    this.events.on('special-drop-picked', this.applySpecialDrop, this);
    this.events.on('partner-heal', this.applyPartnerHeal, this);
    this.events.on('partner-shield', this.addPartnerShield, this);
    this.events.on('player-damage-request', this.applyExternalDamage, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.removeRunEventHandlers, this);
    this.exposeBossLab();
    this.exposeVisualValidationLab();
    this.spawnWaveTreasure();
    if (this.shouldUseThreeBattleView()) {
      this.threeBattleView = new ThreeBattleView(this);
    }

    this.events.emit('m1:game-ready', { player: this.player, enemySystem: this.enemySystem });
  }

  update(timeMs: number, deltaMs: number) {
    this.runState?.update(deltaMs / 1000);
    this.updatePhaseEffects();

    if (this.runState?.getPhase() === 'playing') {
      this.updatePlayerMovement();
      this.applyRelicRuntimeModifiers();
      this.enemySystem?.update(timeMs, deltaMs);
      this.partnerSystem?.update(timeMs);
      this.materialSystem?.update();

      this.updatePlayerDamage(deltaMs);
    } else {
      this.stopPlayer();
      this.enemySystem?.stopAll();
    }

    this.publishRuntimeStats();
    this.updateAutoplayFlow();
    this.updateRunHud();
    this.updateThreeBattleView(timeMs);
  }

  private drawArena() {
    if (this.shouldUseThreeBattleView()) {
      return;
    }

    const bg = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x17140f, 1);
    bg.setDepth(-10);

    const grid = this.add.graphics();
    grid.lineStyle(1, 0x2e281f, 0.58);

    for (let x = 30; x < GAME_WIDTH; x += 30) {
      grid.lineBetween(x, 80, x, GAME_HEIGHT);
    }

    for (let y = 96; y < GAME_HEIGHT; y += 30) {
      grid.lineBetween(0, y, GAME_WIDTH, y);
    }

    grid.lineStyle(2, 0x6d5c38, 0.72);
    grid.strokeRect(8, 86, GAME_WIDTH - 16, GAME_HEIGHT - 94);
  }

  private shouldUseThreeBattleView() {
    return new URLSearchParams(window.location.search).get('legacy') !== '1';
  }

  private hpBarBg?: Phaser.GameObjects.Graphics;
  private hpBarFill?: Phaser.GameObjects.Graphics;

  private createHud() {
    if (this.shouldUseThreeBattleView()) {
      return;
    }

    // Top Bar Background
    const topBar = this.add.rectangle(GAME_WIDTH / 2, 40, GAME_WIDTH, 48, 0x1f1a14, 0.85);
    topBar.setDepth(50);

    // HP Bar
    this.hpBarBg = this.add.graphics();
    this.hpBarBg.fillStyle(0x3a3026, 1);
    this.hpBarBg.fillRoundedRect(12, 28, 140, 24, 4);
    this.hpBarBg.setDepth(51);

    this.hpBarFill = this.add.graphics();
    this.hpBarFill.setDepth(52);

    this.hpText = this.add
      .text(82, 40, '100 / 100', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#ffffff',
        fontStyle: 'bold'
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(53);

    // Wave Text
    this.waveBox = this.add.rectangle(GAME_WIDTH / 2, 40, 80, 24, 0x2e251d, 1);
    this.waveBox.setStrokeStyle(1, 0x4a3c30);
    this.waveBox.setDepth(51);

    this.waveText = this.add
      .text(GAME_WIDTH / 2, 40, 'w1  20.0s', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#d9cba8',
        fontStyle: 'bold'
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(52);

    // Materials Text
    this.materialBox = this.add.rectangle(GAME_WIDTH - 60, 40, 96, 24, 0x2e251d, 1);
    this.materialBox.setStrokeStyle(1, 0x4a3c30);
    this.materialBox.setDepth(51);

    this.materialText = this.add
      .text(GAME_WIDTH - 60, 40, 'M 0  L1', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#88ff88',
        fontStyle: 'bold'
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(52);

    // Minor texts below top bar
    this.enemyText = this.add
      .text(GAME_WIDTH - 12, 70, 'enemies 0', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        color: '#9e8e70',
        align: 'right',
      }))
      .setOrigin(1, 0)
      .setDepth(50);
      
    this.add
      .text(12, 70, 'WASD / arrows / touch joystick', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        color: '#7a6a50',
      }))
      .setDepth(50);
  }

  private updatePlayerMovement() {
    if (!this.player) {
      return;
    }

    this.moveVector.set(0, 0);

    if (this.cursors?.left.isDown || this.wasd?.A.isDown) {
      this.moveVector.x -= 1;
    }

    if (this.cursors?.right.isDown || this.wasd?.D.isDown) {
      this.moveVector.x += 1;
    }

    if (this.cursors?.up.isDown || this.wasd?.W.isDown) {
      this.moveVector.y -= 1;
    }

    if (this.cursors?.down.isDown || this.wasd?.S.isDown) {
      this.moveVector.y += 1;
    }

    const joystickVector = this.joystick?.getVector();
    if (joystickVector && joystickVector.lengthSq() > 0.01) {
      this.moveVector.copy(joystickVector);
    }

    if (this.autoplayConfig.enabled) {
      this.moveVector.copy(this.getAutoplayMoveVector(this.time.now));
    }

    if (this.moveVector.lengthSq() > 1) {
      this.moveVector.normalize();
    }

    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const relicMoveBonus = this.getRelicContextModifiers()?.moveSpeedBonus ?? 0;
    const upgradeMoveBonus = this.runState?.snapshot().upgradeModifiers.moveSpeedBonus ?? 0;
    const temporaryMoveBonus = this.time.now < this.temporaryMoveSpeedExpiresAt ? this.temporaryMoveSpeedBonus : 0;
    const playerSpeed =
      PLAYER_SPEED *
      getTunables().player.speedMultiplier *
      this.playerTemplate.modifiers.moveSpeedMultiplier *
      (1 + relicMoveBonus + upgradeMoveBonus + temporaryMoveBonus);
    body.setVelocity(this.moveVector.x * playerSpeed, this.moveVector.y * playerSpeed);
  }

  private getAutoplayMoveVector(nowMs: number) {
    this.autoplayVector.set(0, 0);
    if (!this.player) {
      return this.autoplayVector;
    }

    const dangerVector = new Phaser.Math.Vector2(0, 0);
    const seekVector = new Phaser.Math.Vector2(0, 0);
    const activeEnemies = this.enemySystem?.getActiveEnemies() ?? [];
    let nearestEnemyDistance = Infinity;

    for (const enemy of activeEnemies) {
      const dx = this.player.x - enemy.x;
      const dy = this.player.y - enemy.y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      nearestEnemyDistance = Math.min(nearestEnemyDistance, distance);
      const isBoss = enemy.getData('bossWave') !== undefined || enemy.radius >= 24;
      const dangerRadius = isBoss ? 190 : 118;
      if (distance < dangerRadius) {
        const weight = ((dangerRadius - distance) / dangerRadius) * (isBoss ? 3.2 : 2.1);
        dangerVector.x += (dx / distance) * weight;
        dangerVector.y += (dy / distance) * weight;
      }
    }

    for (const threat of this.enemySystem?.getActiveBossThreats() ?? []) {
      let dx = this.player.x - threat.x;
      let dy = this.player.y - threat.y;
      let distance = Math.max(1, Math.hypot(dx, dy));
      if (threat.width !== undefined && threat.height !== undefined) {
        const left = threat.x - threat.width / 2;
        const right = threat.x + threat.width / 2;
        const top = threat.y - threat.height / 2;
        const bottom = threat.y + threat.height / 2;
        const closestX = Phaser.Math.Clamp(this.player.x, left, right);
        const closestY = Phaser.Math.Clamp(this.player.y, top, bottom);
        dx = this.player.x - closestX;
        dy = this.player.y - closestY;
        distance = Math.hypot(dx, dy);
        if (distance < 1) {
          const escapeLeft = Math.abs(this.player.x - left);
          const escapeRight = Math.abs(right - this.player.x);
          const escapeTop = Math.abs(this.player.y - top);
          const escapeBottom = Math.abs(bottom - this.player.y);
          const lowest = Math.min(escapeLeft, escapeRight, escapeTop, escapeBottom);
          dx = lowest === escapeLeft ? -1 : lowest === escapeRight ? 1 : 0;
          dy = lowest === escapeTop ? -1 : lowest === escapeBottom ? 1 : 0;
          distance = 1;
        }
      }
      nearestEnemyDistance = Math.min(nearestEnemyDistance, distance);
      const dangerRadius = threat.width !== undefined ? 78 : threat.radius + (threat.source === 'boss-projectile' ? 70 : 48);
      if (distance < dangerRadius) {
        const weight = ((dangerRadius - distance) / dangerRadius) * (threat.source === 'boss-projectile' ? 4 : 3);
        dangerVector.x += (dx / distance) * weight;
        dangerVector.y += (dy / distance) * weight;
      }
    }

    if (Number.isFinite(nearestEnemyDistance)) {
      this.autoplayNearestEnemyMinDistance = Math.min(this.autoplayNearestEnemyMinDistance, nearestEnemyDistance);
    }

    const drops = this.materialSystem?.getActiveDrops() ?? [];
    const shouldSeekDrops = nearestEnemyDistance > 86 || drops.some((drop) => drop.kind !== 'material');
    if (shouldSeekDrops) {
      const nearestDrop = drops
        .map((drop) => ({
          drop,
          distance: Phaser.Math.Distance.Between(this.player!.x, this.player!.y, drop.x, drop.y),
        }))
        .sort((a, b) => {
          const aSpecial = a.drop.kind === 'material' ? 0 : -180;
          const bSpecial = b.drop.kind === 'material' ? 0 : -180;
          return a.distance + aSpecial - (b.distance + bSpecial);
        })[0];
      if (nearestDrop && nearestDrop.distance < 190) {
        seekVector.x += (nearestDrop.drop.x - this.player.x) / Math.max(1, nearestDrop.distance);
        seekVector.y += (nearestDrop.drop.y - this.player.y) / Math.max(1, nearestDrop.distance);
      }
    }

    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT * 0.55;
    const centerDistance = Phaser.Math.Distance.Between(this.player.x, this.player.y, centerX, centerY);
    if (centerDistance > 70) {
      seekVector.x += ((centerX - this.player.x) / centerDistance) * 0.25;
      seekVector.y += ((centerY - this.player.y) / centerDistance) * 0.25;
    }

    const margin = 52;
    if (this.player.x < margin) dangerVector.x += 1.2;
    if (this.player.x > GAME_WIDTH - margin) dangerVector.x -= 1.2;
    if (this.player.y < 110 + margin) dangerVector.y += 1.2;
    if (this.player.y > GAME_HEIGHT - margin) dangerVector.y -= 1.2;

    const orbit = new Phaser.Math.Vector2(Math.cos(nowMs * 0.0017), Math.sin(nowMs * 0.0017)).scale(0.18);
    this.autoplayVector.copy(dangerVector.scale(1.15)).add(seekVector.scale(0.85)).add(orbit);
    if (this.autoplayVector.lengthSq() > 1) {
      this.autoplayVector.normalize();
    }
    this.autoplayMovementSamples += 1;
    return this.autoplayVector;
  }

  private updateAutoplayFlow() {
    if (!this.autoplayConfig.enabled || !this.runState) {
      return;
    }

    const phase = this.runState.getPhase();
    if (this.time.now - this.autoplayLastStatusAt > 1000) {
      this.autoplayLastStatusAt = this.time.now;
      window.__autoPlayStatus = this.buildAutoplayResult(phase === 'victory' || phase === 'dead' ? phase : 'running');
    }

    if (phase === 'levelUp' && this.currentUpgradeOptions.length > 0) {
      const upgrade = this.pickAutoplayUpgrade();
      if (upgrade) {
        this.chooseUpgrade(upgrade);
      }
      return;
    }

    if (phase === 'waveComplete') {
      this.shopSystem?.autoResolve(this.autoplayConfig.strategy, () => this.nextWave());
      return;
    }

    if (phase === 'victory' || phase === 'dead') {
      this.publishAutoplayResult(phase);
    }
  }

  private pickAutoplayUpgrade() {
    const priorities: Record<ShopAutomationStrategy, string[]> = {
      balanced: ['scatter-shot', 'war-drum', 'crit-engine', 'treasure-map', 'damage', 'attack-speed', 'range', 'max-hp', 'move-speed', 'pickup-range', 'crit-chance'],
      star: ['scatter-shot', 'blood-contract', 'crit-engine', 'war-drum', 'damage', 'attack-speed', 'range', 'crit-chance', 'move-speed', 'max-hp', 'pickup-range'],
      relic: ['treasure-map', 'magnet-ritual', 'scatter-shot', 'damage', 'attack-speed', 'range', 'pickup-range', 'move-speed', 'max-hp', 'crit-chance'],
      synergy: ['war-drum', 'crit-engine', 'scatter-shot', 'attack-speed', 'crit-chance', 'damage', 'range', 'move-speed', 'max-hp', 'pickup-range'],
      economy: ['treasure-map', 'magnet-ritual', 'pickup-range', 'move-speed', 'max-hp', 'damage', 'attack-speed', 'range', 'crit-chance'],
    };
    const ordered = priorities[this.autoplayConfig.strategy];
    return [...this.currentUpgradeOptions].sort((a, b) => getUpgradePriority(a.id, ordered) - getUpgradePriority(b.id, ordered))[0];
  }

  private publishAutoplayResult(status: 'victory' | 'dead') {
    if (this.autoplayResultPublished || !this.runState) {
      return;
    }
    this.autoplayResultPublished = true;
    const result = this.buildAutoplayResult(status);
    window.__autoPlayResult = result;
    window.__autoPlayStatus = result;
  }

  private buildAutoplayResult(status: AutoplayResult['status']): AutoplayResult {
    const snapshot = this.runState!.snapshot();
    return {
      status,
      seed: snapshot.seed,
      preset: this.autoplayConfig.preset,
      strategy: this.autoplayConfig.strategy,
      template: snapshot.templateName,
      wave: snapshot.wave,
      hp: snapshot.hp,
      maxHp: snapshot.maxHp,
      materials: snapshot.materials,
      level: snapshot.level,
      elapsedMs: Math.round(this.time.now - this.autoplayStartedAt),
      movementSamples: this.autoplayMovementSamples,
      nearestEnemyMinDistance: Number.isFinite(this.autoplayNearestEnemyMinDistance)
        ? Math.round(this.autoplayNearestEnemyMinDistance)
        : null,
      stats: snapshot.analysis,
    };
  }

  private publishRuntimeStats() {
    const activeEnemies = this.enemySystem?.getActiveCount() ?? 0;
    const enemyPoolSize = this.enemySystem?.getPoolSize() ?? 0;
    this.enemyText?.setText(`enemies ${activeEnemies}`);
    const snapshot = this.runState?.snapshot();
    const materials = snapshot?.materials ?? 0;
    const level = snapshot?.level ?? 1;
    this.materialText?.setText(`M ${materials}  L${level}`);

    this.registry.set('m1.activeEnemies', activeEnemies);
    this.registry.set('m1.enemyPoolSize', enemyPoolSize);
    this.registry.set('m1.materials', materials);
    this.registry.set('m1.level', level);
    this.registry.set('m1.xp', snapshot?.xp ?? 0);
    this.registry.set('m1.nextLevelXp', snapshot?.nextLevelXp ?? 0);
    const runStats = snapshot?.analysis;
    this.registry.set('m8.runStats', runStats);
    window.__runStats = runStats;
    this.registry.set('m1.maxHp', snapshot?.maxHp ?? 100);
    this.registry.set('m3.upgradeModifiers', snapshot?.upgradeModifiers ?? {
      damageBonus: 0,
      attackRangeBonus: 0,
      attackSpeedBonus: 0,
      moveSpeedBonus: 0,
      maxHpDelta: 0,
      pickupRangeBonus: 0,
      critChanceBonus: 0,
    });
  }

  private updateThreeBattleView(timeMs: number) {
    if (!this.threeBattleView || !this.player || !this.runState) {
      return;
    }
    this.threeBattleView.update({
      player: this.player,
      enemies: this.enemySystem?.getActiveEnemies() ?? [],
      partners: this.partnerSystem?.getPartnerVisuals() ?? [],
      projectiles: this.partnerSystem?.getProjectileVisuals() ?? [],
      drops: this.materialSystem?.getDropVisuals() ?? [],
      snapshot: this.runState.snapshot(),
    }, timeMs);
  }

  private updatePlayerDamage(deltaMs: number) {
    if (!this.player || !this.runState) {
      return;
    }

    this.damageCooldownMs = Math.max(0, this.damageCooldownMs - deltaMs);
    if (this.damageCooldownMs > 0) {
      return;
    }

    const activeEnemies = this.enemySystem?.getActiveEnemies() ?? [];
    for (const enemy of activeEnemies) {
      const hitDistance = PLAYER_RADIUS + enemy.radius;
      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, enemy.x, enemy.y);
      if (distance > hitDistance) {
        continue;
      }

      const blocked = this.partnerSystem?.tryTankBlock() ?? false;
      const damageMultiplier = this.partnerSystem?.getDamageTakenMultiplier() ?? 1;
      if (this.tryPartnerShield() || this.tryRelicShield()) {
        this.showRelicShield();
        this.damageCooldownMs = 650;
        return;
      }
      const source = (enemy.getData('damageSource') as DamageSource | undefined) ?? 'collision';
      const baseDamage = (enemy.getData('collisionDamage') as number | undefined) ?? 12;
      this.runState.applyDamage((blocked ? baseDamage * 0.5 : baseDamage) * damageMultiplier, source);
      const thornDamage = this.getRelicContextModifiers()?.thornDamage ?? 0;
      if (thornDamage > 0 && enemy.active) {
        this.enemySystem?.takeDamage(enemy, thornDamage, 'relic');
      }
      this.damageCooldownMs = 650;
      this.flashPlayer();
      return;
    }
  }

  private flashPlayer() {
    if (!this.player) {
      return;
    }

    // Camera shake and flash on hit
    this.cameras.main.shake(150, 0.008);
    this.cameras.main.flash(150, 200, 0, 0);

    this.tweens.add({
      targets: this.player,
      alpha: 0.35,
      duration: 70,
      yoyo: true,
      repeat: 2,
      onComplete: () => this.player?.setAlpha(1),
    });
  }

  private updateRunHud() {
    const snapshot = this.runState?.snapshot();
    if (!snapshot) {
      return;
    }

    // Update HP Graphic and text
    const currentHp = Math.ceil(snapshot.hp);
    this.hpText?.setText(`${currentHp} / ${snapshot.maxHp}`);
    if (this.hpBarFill) {
      this.hpBarFill.clear();
      const fillRatio = Math.max(0, currentHp / snapshot.maxHp);
      const fillColor = fillRatio > 0.25 ? 0x42b85d : 0xc63b3b;
      this.hpBarFill.fillStyle(fillColor, 1);
      this.hpBarFill.fillRoundedRect(12, 28, Math.max(1, 140 * fillRatio), 24, 4);
    }

    const maxWaveLabel = snapshot.maxWave > 0 ? String(snapshot.maxWave) : '∞';
    this.waveText?.setText(`w${snapshot.wave}/${maxWaveLabel}  ${snapshot.waveTimeLeft.toFixed(1)}s`);
    
    this.registry.set('m1.phase', snapshot.phase);
    this.registry.set('m1.wave', snapshot.wave);
    this.registry.set('m1.hp', snapshot.hp);
    this.registry.set('m1.seed', snapshot.seed);
    this.registry.set('m1.maxWave', snapshot.maxWave);
    this.registry.set('m1.template', snapshot.templateName);
    this.registry.set('m3.upgradeModifiers', snapshot.upgradeModifiers);

    this.debugOverlay?.update(snapshot, {
      fps: this.game.loop.actualFps,
      enemyCount: this.enemySystem?.getActiveCount() ?? 0,
      objectPoolCount: this.enemySystem?.getPoolSize() ?? 0,
    });
  }

  private updatePhaseEffects() {
    const phase = this.runState?.getPhase();
    if (!phase || phase === this.lastPhase) {
      return;
    }

    this.lastPhase = phase;
    this.events.emit('m1:phase-changed', phase);

    if (phase === 'playing') {
      this.hideStatusPanel();
      this.hideUpgradePanel();
      this.shopSystem?.hide();
      return;
    }

    if (phase === 'levelUp') {
      this.shopSystem?.hide();
      this.hideStatusPanel();
      this.showUpgradePanel();
      return;
    }

    if (phase === 'dead') {
      this.shopSystem?.hide();
      this.hideUpgradePanel();
      this.captureRunStats('final');
      this.showStatusPanel('阵亡', '重新开始', () => this.restartRun());
      return;
    }

    if (phase === 'waveComplete') {
      this.hideUpgradePanel();
      this.enemySystem?.clearAll();
      this.materialSystem?.clearAllAndCollect();
      this.applyWaveCompleteRelics();
      this.captureRunStats('wave-complete');
      this.hideStatusPanel();
      this.shopSystem?.openForWave(this.runState?.wave ?? 1, () => this.nextWave());
      return;
    }

    if (phase === 'victory') {
      this.shopSystem?.hide();
      this.hideUpgradePanel();
      this.enemySystem?.clearAll();
      this.materialSystem?.clearAllAndCollect();
      this.captureRunStats('final');
      this.showStatusPanel('通关', '重新开始', () => this.restartRun());
    }
  }

  private showStatusPanel(title: string, actionLabel: string, action: () => void) {
    this.hideStatusPanel();
    const hasStats = title === '阵亡' || title === '通关';

    if (hasStats && window.__runStats) {
      // Use DOM-based PostGameRecap
      this.postGameRecap?.show(window.__runStats as any, title === '通关', action);
      return;
    }

    // Fallback for simple status (like wave complete or pause)
    const panel = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    const panelHeight = 152;
    const background = this.add.rectangle(0, 0, 286, panelHeight, 0x241f19, 0.94);
    background.setStrokeStyle(2, 0xe7b957, 0.9);

    const titleText = this.add
      .text(0, -44, title, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '24px',
        color: '#f4e8cf',
      }))
      .setOrigin(0.5);

    const buttonY = 34;
    const button = this.add.rectangle(0, buttonY, 178, 48, 0xe7b957, 1);
    button.setStrokeStyle(2, 0xf7df9a, 0.85);
    button.setInteractive({ useHandCursor: true });

    const buttonText = this.add
      .text(0, 34, actionLabel, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '18px',
        color: '#24180a',
      }))
      .setOrigin(0.5);
    buttonText.setY(buttonY);
    buttonText.setInteractive({ useHandCursor: true });

    button.on('pointerdown', action);
    buttonText.on('pointerdown', action);

    const children: Phaser.GameObjects.GameObject[] = [background, titleText, button, buttonText];

    panel.add(children);
    panel.setDepth(200);
    this.statusPanel = panel;
  }

  private hideStatusPanel() {
    this.statusPanel?.destroy(true);
    this.statusPanel = undefined;
  }

  private showUpgradePanel() {
    if (!this.runState || !this.upgradeSystem) {
      return;
    }

    if (this.upgradePanel && this.currentUpgradeOptions.length > 0) {
      return;
    }

    this.hideUpgradePanel();
    this.currentUpgradeOptions = this.upgradeSystem.pickOptions(4);
    const snapshot = this.runState.snapshot();
    const panel = this.add.container(0, 0);
    panel.setDepth(240);

    const shade = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x080705, 0.78);
    const background = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 332, 514, 0x201a14, 0.96);
    background.setStrokeStyle(2, 0xe0b15a, 0.92);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 220, `升级 Lv.${snapshot.level}`, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      color: '#f5e5c8',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    const hint = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 192, '选择一张构筑卡，立即生效', ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color: '#d9cba8',
    })).setOrigin(0.5);

    const children: Phaser.GameObjects.GameObject[] = [shade, background, title, hint];
    this.currentUpgradeOptions.forEach((option, index) => {
      const y = GAME_HEIGHT / 2 - 126 + index * 88;
      const card = this.add.rectangle(GAME_WIDTH / 2, y, 286, 72, option.color, 0.95);
      card.setStrokeStyle(2, 0xffffff, 0.72);
      card.setInteractive({ useHandCursor: true });
      const name = this.add.text(GAME_WIDTH / 2 - 118, y - 22, option.name, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '20px',
        color: '#18110a',
        fontStyle: 'bold',
      }));
      const desc = this.add.text(GAME_WIDTH / 2 - 118, y + 6, option.description, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#18110a',
        wordWrap: { width: 238 },
      }));
      const apply = () => this.chooseUpgrade(option);
      card.on('pointerdown', apply);
      name.setInteractive({ useHandCursor: true }).on('pointerdown', apply);
      desc.setInteractive({ useHandCursor: true }).on('pointerdown', apply);
      card.on('pointerover', () => card.setScale(1.03));
      card.on('pointerout', () => card.setScale(1));
      children.push(card, name, desc);
    });

    panel.add(children);
    panel.setAlpha(0);
    panel.setScale(0.96);
    this.tweens.add({
      targets: panel,
      alpha: 1,
      scale: 1,
      duration: 180,
      ease: 'Back.easeOut',
    });
    this.upgradePanel = panel;
  }

  private chooseUpgrade(option: UpgradeOption) {
    if (!this.runState || !this.upgradeSystem) {
      return;
    }
    this.upgradeSystem.apply(this.runState, option);
    const snapshot = this.runState.snapshot();
    this.runState.recordRunPurchase({
      wave: snapshot.wave,
      kind: 'upgrade',
      itemId: option.id,
      label: option.name,
      cost: 0,
      refund: 0,
      materialsAfter: snapshot.materials,
      note: option.description,
    });
    this.maybeDropUpgradeReward();
    const remaining = this.runState.consumeLevelUp();
    this.hideUpgradePanel();
    if (remaining > 0) {
      this.showUpgradePanel();
      return;
    }
    this.lastPhase = undefined;
  }

  private hideUpgradePanel() {
    this.upgradePanel?.destroy(true);
    this.upgradePanel = undefined;
    this.currentUpgradeOptions = [];
  }

  private maybeDropUpgradeReward() {
    if (!this.player || !this.upgradeSystem || !this.materialSystem) {
      return;
    }
    const kind = this.upgradeSystem.rollUpgradeReward(getTunables().specialDrops.upgradeDropChance);
    if (!kind) {
      return;
    }
    const angle = this.time.now * 0.0037;
    const x = Phaser.Math.Clamp(this.player.x + Math.cos(angle) * 44, 24, GAME_WIDTH - 24);
    const y = Phaser.Math.Clamp(this.player.y + Math.sin(angle) * 44, 100, GAME_HEIGHT - 58);
    this.materialSystem.dropUpgradeReward(x, y, kind);
  }

  private spawnWaveTreasure() {
    if (!this.player || !this.runState || !this.materialSystem) {
      return;
    }
    this.materialSystem.maybeDropTreasureChest(this.runState.wave, this.player.x, this.player.y);
  }

  private showPickupText(x: number, y: number, label: string, color: string) {
    const text = this.add.text(x, y - 18, label, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color,
      fontStyle: 'bold',
    })).setOrigin(0.5).setDepth(80);
    this.tweens.add({
      targets: text,
      y: y - 40,
      alpha: 0,
      duration: 780,
      onComplete: () => text.destroy(),
    });
  }

  private nextWave() {
    this.captureRunStats('shop-complete');
    this.shopSystem?.hide();
    const started = this.runState?.nextWave() ?? false;
    if (started) {
      this.enemySystem?.startWave(this.runState?.wave ?? 1);
      this.spawnWaveTreasure();
    }
    this.damageCooldownMs = 0;
  }

  private restartRun() {
    this.scene.restart();
  }

  private stopPlayer() {
    if (!this.player) {
      return;
    }
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, 0);
  }

  private recordDamageDealt(payload: number | EnemyDamagePayload) {
    const amount = typeof payload === 'number' ? payload : payload.amount;
    const source = typeof payload === 'number' ? undefined : payload.source;
    this.runState?.recordDamageDealt(amount, source);
  }

  private recordEnemyDefeated(payload?: { source?: PartnerType | 'relic' }) {
    this.runState?.recordEnemyDefeated(payload?.source);
  }

  private recordBossDefeated(payload: BossDeathPayload) {
    this.runState?.recordEnemyDefeated(payload.source);
    for (let index = 0; index < 12; index += 1) {
      const angle = (Math.PI * 2 * index) / 12;
      this.materialSystem?.dropMaterial(payload.x + Math.cos(angle) * 18, payload.y + Math.sin(angle) * 18);
    }
    if (payload.mode !== 'wave' || payload.wave !== 10) {
      return;
    }
    this.runState?.markVictory();
  }

  private recordMaterialPickup(amount: number) {
    this.runState?.recordMaterialPickup(amount);
  }

  private applySpecialDrop(payload: SpecialDropPickupPayload) {
    if (!this.runState) {
      return;
    }

    if (payload.kind === 'heal-orb') {
      this.runState.recover(payload.value);
      this.recordSpecialDropPickup(payload);
      this.showPickupText(payload.x, payload.y, `+${payload.value} HP`, '#7cff9a');
      return;
    }

    if (payload.kind === 'xp-orb') {
      this.runState.addBonusXp(payload.value);
      this.recordSpecialDropPickup(payload);
      this.showPickupText(payload.x, payload.y, `+${payload.value} XP`, '#ffe16a');
      return;
    }

    if (payload.kind === 'haste-orb') {
      this.temporaryMoveSpeedBonus = payload.value;
      this.temporaryMoveSpeedExpiresAt = this.time.now + getTunables().specialDrops.hasteDurationMs;
      this.recordSpecialDropPickup(payload);
      this.showPickupText(payload.x, payload.y, '加速', '#7fd7ff');
      return;
    }

    if (payload.kind === 'material-crate') {
      this.runState.addMaterials(payload.value);
      this.recordSpecialDropPickup(payload);
      this.showPickupText(payload.x, payload.y, `+${payload.value} 材料`, '#ffd06a');
      return;
    }

    if (payload.kind === 'upgrade-chest') {
      const option = this.upgradeSystem?.pickInstantUpgrade();
      if (!option) {
        return;
      }
      this.runState.applyUpgradeBonus(option.id);
      this.recordSpecialDropPickup(payload, option.name);
      this.showPickupText(payload.x, payload.y, option.name, '#efc5ff');
    }
  }

  private recordSpecialDropPickup(payload: SpecialDropPickupPayload, label: string = payload.kind) {
    if (!this.runState) {
      return;
    }
    const snapshot = this.runState.snapshot();
    this.runState.recordRunPurchase({
      wave: snapshot.wave,
      kind: 'special-drop',
      itemId: payload.kind,
      label,
      cost: 0,
      refund: 0,
      materialsAfter: snapshot.materials,
      note: `${payload.value}`,
    });
  }

  private applyPartnerHeal(amount: number) {
    this.runState?.heal(amount);
  }

  private addPartnerShield(amount: number) {
    if (!Number.isInteger(amount) || amount <= 0) {
      throw new Error(`Invalid partner shield amount: ${amount}`);
    }
    this.partnerShieldCharges = Math.min(3, this.partnerShieldCharges + amount);
  }

  private applyExternalDamage(payload: { amount: number; source: DamageSource }) {
    if (!this.runState || this.runState.getPhase() !== 'playing') {
      return;
    }
    if (this.tryPartnerShield() || this.tryRelicShield()) {
      this.showRelicShield();
      this.damageCooldownMs = 400;
      return;
    }
    const blocked = this.partnerSystem?.tryTankBlock() ?? false;
    const damageMultiplier = this.partnerSystem?.getDamageTakenMultiplier() ?? 1;
    this.runState.applyDamage((blocked ? payload.amount * 0.5 : payload.amount) * damageMultiplier, payload.source);
    this.damageCooldownMs = 400;
    this.flashPlayer();
  }

  // @ts-ignore
  private formatRunSummary(snapshot: ReturnType<RunState['snapshot']>) {
    const deathStats = snapshot.deathStats;
    const sourceLabel = deathStats.lastHitSource === 'shockwave'
      ? '冲击波'
      : deathStats.lastHitSource === 'minion'
        ? '小怪'
        : deathStats.lastHitSource === 'boss-hazard'
          ? 'Boss危险区'
          : deathStats.lastHitSource === 'boss-projectile'
            ? 'Boss弹幕'
        : deathStats.lastHitSource === 'collision'
          ? '碰撞'
          : '-';
    const topDamage = snapshot.analysis.topDamageSource
      ? `${snapshot.analysis.topDamageSource.label} ${snapshot.analysis.topDamageSource.damage.toFixed(0)}`
      : '-';
    const topTaken = snapshot.analysis.topDamageTakenSource
      ? `${snapshot.analysis.topDamageTakenSource.label} ${snapshot.analysis.topDamageTakenSource.damage.toFixed(0)}`
      : '-';
    return [
      `模板 ${snapshot.templateName}  波次 ${snapshot.wave}/${snapshot.maxWave > 0 ? snapshot.maxWave : '∞'}`,
      `伤害 ${snapshot.stats.damageDealt.toFixed(0)}  击杀 ${snapshot.stats.enemiesDefeated}`,
      `主力 ${topDamage}  主要承伤 ${topTaken}`,
      `受击 ${snapshot.stats.hitsTaken}  承伤 ${snapshot.stats.damageTaken.toFixed(0)}`,
      `材料 ${snapshot.stats.materialsPicked}  等级 ${snapshot.level}`,
      `死因 ${sourceLabel}`,
      `碰撞 ${deathStats.collisionHits}  冲击波 ${deathStats.shockwaveHits}  危险区 ${deathStats.bossHazardHits}  弹幕 ${deathStats.bossProjectileHits}  小怪 ${deathStats.minionHits}`,
      ].join('\n');
  }

  private getRelicContextModifiers() {
    const snapshot = this.runState?.snapshot();
    return this.relicSystem?.getModifiers({
      wave: snapshot?.wave ?? 1,
      materials: snapshot?.materials ?? 0,
      hp: snapshot?.hp ?? 100,
      maxHp: snapshot?.maxHp ?? 100,
      partnerTypes: this.partnerSystem?.getPartnerTypes() ?? [],
    });
  }

  private applyRelicRuntimeModifiers() {
    if (!this.runState || !this.materialSystem) {
      return;
    }
    const modifiers = this.getRelicContextModifiers();
    if (!modifiers) {
      return;
    }
    this.runState.setRelicModifiers({
      maxHpDelta: modifiers.maxHpDelta,
      materialGainBonus: modifiers.materialGainBonus,
      xpGainBonus: modifiers.xpGainBonus,
    });
    this.materialSystem.setPickupRadiusMultiplier(
      this.playerTemplate.modifiers.pickupRadiusMultiplier *
        (1 + modifiers.pickupRangeBonus + this.runState.snapshot().upgradeModifiers.pickupRangeBonus),
    );
  }

  private applyWaveCompleteRelics() {
    if (!this.runState || !this.relicSystem) {
      return;
    }
    const snapshot = this.runState.snapshot();
    this.relicSystem.applyWaveComplete(this.runState, snapshot.wave, {
      wave: snapshot.wave,
      materials: snapshot.materials,
      hp: snapshot.hp,
      maxHp: snapshot.maxHp,
      partnerTypes: this.partnerSystem?.getPartnerTypes() ?? [],
    });
  }

  private tryRelicShield() {
    if (!this.relicSystem || !this.runState) {
      return false;
    }
    const snapshot = this.runState.snapshot();
    return this.relicSystem.tryShield(this.time.now, {
      wave: snapshot.wave,
      materials: snapshot.materials,
      hp: snapshot.hp,
      maxHp: snapshot.maxHp,
      partnerTypes: this.partnerSystem?.getPartnerTypes() ?? [],
    });
  }

  private tryPartnerShield() {
    if (this.partnerShieldCharges <= 0) {
      return false;
    }
    this.partnerShieldCharges -= 1;
    return true;
  }

  private showRelicShield() {
    if (!this.player) {
      return;
    }
    const shield = this.add.circle(this.player.x, this.player.y, 42, 0xcfd7d9, 0.2);
    shield.setStrokeStyle(3, 0xcfd7d9, 0.8);
    shield.setDepth(45);
    this.tweens.add({
      targets: shield,
      scale: 1.35,
      alpha: 0,
      duration: 260,
      onComplete: () => shield.destroy(),
    });
  }

  private removeRunEventHandlers() {
    this.events.off('enemy-damaged', this.recordDamageDealt, this);
    this.events.off('enemy-death-stats', this.recordEnemyDefeated, this);
    this.events.off('boss-died', this.recordBossDefeated, this);
    this.events.off('material-picked', this.recordMaterialPickup, this);
    this.events.off('special-drop-picked', this.applySpecialDrop, this);
    this.events.off('partner-heal', this.applyPartnerHeal, this);
    this.events.off('partner-shield', this.addPartnerShield, this);
    this.events.off('player-damage-request', this.applyExternalDamage, this);
    this.hideUpgradePanel();
    this.threeBattleView?.destroy();
    this.devTuningMenu?.destroy();
    this.postGameRecap?.hide();
    this.tooltipOverlay?.destroy();
    this.devTuningMenu = undefined;
    this.threeBattleView = undefined;
    window.animeBrotatoBossLab = undefined;
    window.animeBrotatoVisualLab = undefined;
    window.__runStats = undefined;
  }

  private exposeBossLab() {
    window.animeBrotatoBossLab = {
      spawn: (type, count = 1) => {
        if (count === 1) {
          return this.enemySystem?.spawnBossForDebug(type);
        }
        return this.enemySystem?.spawnMultipleBossesForDebug(type, count);
      },
      clear: () => this.enemySystem?.clearBossesForDebug(),
      setMultipliers: (hpMultiplier, speedMultiplier) => this.enemySystem?.setDebugBossMultipliers(hpMultiplier, speedMultiplier),
    };
  }

  private exposeVisualValidationLab() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('visualTest') !== '1') {
      window.animeBrotatoVisualLab = undefined;
      return;
    }

    window.animeBrotatoVisualLab = {
      levelUp: () => {
        if (!this.runState) {
          throw new Error('Run state is not ready');
        }
        this.runState.addBonusXp(this.runState.getNextLevelXp());
        return this.runState.snapshot();
      },
      addMaterials: (amount = 20) => {
        if (!this.runState) {
          throw new Error('Run state is not ready');
        }
        this.runState.addMaterials(amount);
        return this.runState.snapshot();
      },
      bossPressure: (type = 'rift-core', count = 3) => {
        const bosses = this.enemySystem?.spawnMultipleBossesForDebug(type, count) ?? [];
        return { count: bosses.length };
      },
    };
  }

  private captureRunStats(stage: 'wave-complete' | 'shop-complete' | 'final') {
    if (!this.runState) {
      return undefined;
    }
    const snapshot = this.runState.snapshot();
    const relicContext = {
      wave: snapshot.wave,
      materials: snapshot.materials,
      hp: snapshot.hp,
      maxHp: snapshot.maxHp,
      partnerTypes: this.partnerSystem?.getPartnerTypes() ?? [],
    };
    const relicContributions = new Map(
      this.relicSystem?.getContributionSummary(relicContext).map((contribution) => [contribution.id, contribution]) ?? [],
    );
    this.runState.recordBuildSnapshot({
      wave: snapshot.wave,
      stage,
      hp: snapshot.hp,
      maxHp: snapshot.maxHp,
      materials: snapshot.materials,
      level: snapshot.level,
      partners: this.partnerSystem?.getPartnerSlots() ?? [],
      relics: this.relicSystem?.getOwnedStacks().map((stack) => ({
        id: stack.id,
        name: stack.definition.name,
        count: stack.count,
        rarity: stack.definition.rarity,
        tag: stack.definition.tag,
        contribution: relicContributions.get(stack.id)
          ? {
              damageBonus: relicContributions.get(stack.id)?.damageBonus ?? 0,
              attackSpeedBonus: relicContributions.get(stack.id)?.attackSpeedBonus ?? 0,
              maxHpDelta: relicContributions.get(stack.id)?.maxHpDelta ?? 0,
              materialGainBonus: relicContributions.get(stack.id)?.materialGainBonus ?? 0,
            }
          : undefined,
      })) ?? [],
      synergies: this.partnerSystem?.getSynergyState().snapshots ?? [],
      upgradeModifiers: snapshot.upgradeModifiers,
    });
    const latest = this.runState.snapshot().analysis;
    window.__runStats = latest;
    return latest;
  }
}

declare global {
  interface Window {
    animeBrotatoBossLab?: {
      spawn: (type: import('../systems/bossCatalog').BossType, count?: number) => unknown;
      clear: () => void;
      setMultipliers: (hpMultiplier: number, speedMultiplier: number) => void;
    };
    animeBrotatoVisualLab?: {
      levelUp: () => ReturnType<RunState['snapshot']>;
      addMaterials: (amount?: number) => ReturnType<RunState['snapshot']>;
      bossPressure: (type?: import('../systems/bossCatalog').BossType, count?: number) => { count: number };
    };
    __runStats?: ReturnType<RunState['getRunStats']>;
    __autoPlayStatus?: AutoplayResult;
    __autoPlayResult?: AutoplayResult;
  }
}

function getAutoplayConfig(): AutoplayConfig {
  const params = new URLSearchParams(window.location.search);
  const strategy = params.get('strategy') ?? 'balanced';
  return {
    enabled: params.get('autoplay') === '1',
    strategy: isAutoplayStrategy(strategy) ? strategy : 'balanced',
    preset: params.get('preset') ?? 'default',
  };
}

function getUpgradePriority(id: string, ordered: string[]) {
  const index = ordered.indexOf(id);
  return index >= 0 ? index : ordered.length;
}

function isAutoplayStrategy(value: string): value is ShopAutomationStrategy {
  return value === 'balanced' || value === 'star' || value === 'relic' || value === 'synergy' || value === 'economy';
}
